// src/features/executive/sales/widgets/CategoryMixCard.tsx
// Revenue share by treatment type — one slice per `operative_types` row
// (settings → treatment types) as returned by the backend, no keyword
// folding. Paid treatments with no type arrive as the "other" (untyped)
// slice; clicking any slice drills into the procedures behind it.
import { Box, Chip, Stack, Typography } from '@mui/material'
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined'
import { useTranslation } from 'react-i18next'
import { useMemo } from 'react'
import { Doughnut } from 'react-chartjs-2'
import {
  ArcElement,
  Chart as ChartJS,
  Legend,
  Tooltip as ChartTooltip,
} from 'chart.js'
import { useDispatch } from 'react-redux'
import { pushDrill } from 'app/slice/executive.slice'
import WidgetCard from 'features/executive/components/WidgetCard/WidgetCard'
import { useCategoryMix } from '../hooks/use-category-mix'
import { formatCurrency, formatVariance } from 'utils/executive.utils'
import type { CategoryMixItem } from 'api/executive/executive.types'
import { shouldUseBar, sortByValueDesc } from 'features/executive/shared/utils/chart-mode'
import { masterSliceColor, masterSliceLabel, OTHER_SLICE_KEY } from 'features/executive/shared/utils/master-slice'
import CategoricalBar, { CategoricalBarRow } from 'features/executive/insight/widgets/CategoricalBar'

ChartJS.register(ArcElement, ChartTooltip, Legend)

// Surface the untyped share once it is large enough to distort the picture,
// so an admin knows to drill in and assign types (settings → treatments).
const UNTYPED_WARNING_PCT = 25

// The drill lists every procedure behind a slice; `txCategoryId: 0` is the
// backend's "no operative type" filter for the untyped slice.
const drillFiltersFor = (c: CategoryMixItem) => ({ txCategoryId: c.operativeTypeId ?? 0 })

function varianceOf(current: number, previous: number | null | undefined): number | null {
  if (previous == null || previous === 0) return null
  return (current - previous) / Math.abs(previous)
}

export default function CategoryMixCard() {
  const { t, i18n } = useTranslation()
  const language = i18n.language
  const dispatch = useDispatch()
  const { data, loading, error, refetch } = useCategoryMix()

  const title = t('EXECUTIVE.SALES.CATEGORY_MIX.TITLE')
  const isEmpty = !data || data.total === 0

  const handleCardClick = (e: React.MouseEvent) => {
    if ((e.target as HTMLElement).tagName === 'CANVAS') return
    dispatch(pushDrill({ id: 'category-mix', label: title }))
  }

  const drillInto = (c: CategoryMixItem) => {
    dispatch(pushDrill({
      id: 'top-procedures',
      label: `${title} · ${masterSliceLabel(c, language)}`,
      scopedFilters: drillFiltersFor(c),
    }))
  }

  const chartData = useMemo(() => {
    if (!data) return null
    return {
      labels: data.categories.map((c) => masterSliceLabel(c, language)),
      datasets: [
        {
          data: data.categories.map((c) => c.amount),
          backgroundColor: data.categories.map((c, i) => masterSliceColor(c, i)),
        },
      ],
    }
  }, [data, language])

  const untyped = data?.categories.find((c) => c.key === OTHER_SLICE_KEY)
  const showUntypedWarning = (untyped?.percent ?? 0) > UNTYPED_WARNING_PCT

  const notice = showUntypedWarning && untyped ? (
    <Chip
      icon={<WarningAmberOutlinedIcon />}
      label={
        <Typography variant="caption" sx={{ whiteSpace: 'normal' }}>
          {t(
            'EXECUTIVE.SALES.CATEGORY_MIX.OTHER_WARNING',
            'หัตถการที่ยังไม่ระบุประเภท {{pct}}% — กดที่แถวเพื่อดูรายการ',
            { pct: untyped.percent.toFixed(0) }
          )}
        </Typography>
      }
      color="warning"
      variant="outlined"
      size="small"
      onClick={(e) => { e.stopPropagation(); drillInto(untyped) }}
      sx={{ height: 'auto', '& .MuiChip-label': { px: 1, py: 0.25 } }}
    />
  ) : null

  // Every type with revenue is shown, so the row count is unbounded — switch
  // to bars past the shared threshold. Guard on !isEmpty so the all-zero
  // case still falls through to the empty state.
  const useBar = !isEmpty && shouldUseBar(data?.categories.length ?? 0)

  const barRows: CategoricalBarRow[] = data
    ? sortByValueDesc(
        data.categories.map((c, i) => ({
          key: c.key,
          label: masterSliceLabel(c, language),
          count: c.amount,
          percent: c.percent,
          previousCount: c.previousAmount,
          color: masterSliceColor(c, i),
        })),
        (r) => r.count,
      )
    : []

  const handleRowDrill = (row: CategoricalBarRow) => {
    const c = data?.categories.find((x) => x.key === row.key)
    if (c) drillInto(c)
  }

  const tooltip = t(
    'EXECUTIVE.SALES.CATEGORY_MIX.TOOLTIP',
    'สัดส่วนรายได้หัตถการ แบ่งตามประเภทหัตถการที่ตั้งค่าไว้ใน ตั้งค่า → ประเภทหัตถการ\nหัตถการที่ยังไม่ได้ระบุประเภทจะรวมอยู่ใน "ยังไม่ระบุประเภท" — กดเพื่อดูรายการ'
  )
  const emptyMessage = t('EXECUTIVE.SALES.CATEGORY_MIX.EMPTY', 'ไม่พบรายได้หัตถการในช่วงเวลาและสาขาที่เลือก')

  if (useBar) {
    return (
      <CategoricalBar
        title={title}
        titleTooltip={tooltip}
        subtitle={`${t('EXECUTIVE.SALES.CATEGORY_MIX.TOTAL')}: ${formatCurrency(data?.total ?? 0)}`}
        notice={notice}
        rows={barRows}
        formatValue={(r) => `${formatCurrency(r.count)} (${r.percent.toFixed(1)}%)`}
        onRowClick={handleRowDrill}
        emptyMessage={emptyMessage}
        loading={loading}
        error={error}
        onRetry={refetch}
      />
    )
  }

  return (
    <WidgetCard
      title={title}
      titleTooltip={tooltip}
      notice={notice}
      loading={loading}
      error={error}
      empty={isEmpty}
      emptyMessage={emptyMessage}
      onRetry={refetch}
      onClick={handleCardClick}
      skeletonHeight={380}
    >
      {data && (
        <>
          <Box sx={{ height: 200, position: 'relative', mb: 2 }}>
            <Doughnut
              data={chartData as any}
              options={{
                maintainAspectRatio: false,
                responsive: true,
                cutout: '70%',
                plugins: {
                  legend: { display: false },
                  tooltip: {
                    callbacks: {
                      label: (ctx) => {
                        const value = Number(ctx.parsed)
                        const pct = data.total > 0 ? (value / data.total) * 100 : 0
                        return `${ctx.label}: ${formatCurrency(value)} (${pct.toFixed(1)}%)`
                      },
                    },
                  },
                },
                onClick: (_evt, elements) => {
                  if (!elements || elements.length === 0) return
                  const category = data?.categories[elements[0].index]
                  if (category) drillInto(category)
                },
              }}
            />
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                pointerEvents: 'none',
              }}
            >
              <Typography variant="caption" color="text.secondary">
                {t('EXECUTIVE.SALES.CATEGORY_MIX.TOTAL')}
              </Typography>
              <Typography variant="h6">{formatCurrency(data.total)}</Typography>
            </Box>
          </Box>
          <Stack spacing={0.5}>
            {data.categories.map((c, i) => {
              const variance = varianceOf(c.amount, c.previousAmount)
              return (
                <Box
                  key={c.key}
                  onClick={(e) => { e.stopPropagation(); drillInto(c) }}
                  sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer' }}
                >
                  <Box
                    sx={{
                      width: 10,
                      height: 10,
                      borderRadius: '50%',
                      backgroundColor: masterSliceColor(c, i),
                      flexShrink: 0,
                    }}
                  />
                  <Typography variant="body2" sx={{ flex: 1 }}>
                    {masterSliceLabel(c, language)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {formatCurrency(c.amount)} ({c.percent.toFixed(1)}%)
                  </Typography>
                  {variance != null && (
                    <Typography
                      variant="caption"
                      color={variance >= 0 ? 'success.main' : 'error.main'}
                      sx={{ minWidth: 56, textAlign: 'right' }}
                    >
                      {formatVariance(variance)}
                    </Typography>
                  )}
                </Box>
              )
            })}
          </Stack>
        </>
      )}
    </WidgetCard>
  )
}
